import { Bar, Eyebrow, ghostButton, Screen, Title } from '../components/ui';
import { today } from '../engine/dates';
import { BADGES, weekMarks } from '../engine/progress';
import { useApp } from '../store/useApp';

export function Wins({ onOpenSettings }: { onOpenSettings: () => void }) {
  const { state } = useApp();
  const { progress } = state;
  const week = weekMarks(progress, today());
  const earned = new Set(progress.earnedBadges);

  // The strip always ends on today, so the letters have to be derived rather
  // than hardcoded to a Monday start.
  const dayLetters = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][d.getDay()]!;
  });

  return (
    <Screen>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Title>Wins</Title>
        <div style={{ font: '400 13px/1.4 var(--font-body)', color: 'var(--text-4)' }}>
          What you have actually done. Not a score.
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: 18,
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(155deg,#2a2620,#1a1f24 65%)',
          border: '1px solid oklch(0.82 0.17 65 / .25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <div
            className="num"
            style={{
              font: '800 44px/.9 var(--font-display)',
              letterSpacing: '-.03em',
              color: 'var(--amber)',
            }}
          >
            {progress.streak}
          </div>
          <div
            style={{
              font: '500 13px/1.3 var(--font-body)',
              color: 'var(--text-3)',
              paddingBottom: 4,
            }}
          >
            days running
            <br />
            longest was {progress.longest}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {week.map((mark, i) => (
            <div
              key={i}
              title={mark === 'on' ? 'Practised' : mark === 'freeze' ? 'Skip day used' : 'No practice'}
              style={{
                flex: 1,
                height: 30,
                borderRadius: 7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                font: '700 11px/1 var(--font-display)',
                background:
                  mark === 'on'
                    ? 'oklch(0.82 0.17 130 / .22)'
                    : mark === 'freeze'
                      ? 'oklch(0.82 0.17 65 / .16)'
                      : 'rgba(255,255,255,.05)',
                border: `1px solid ${
                  mark === 'on'
                    ? 'var(--lime-line)'
                    : mark === 'freeze'
                      ? 'var(--amber-line)'
                      : 'var(--line-strong)'
                }`,
                color: mark === 'freeze' ? 'var(--amber)' : mark === 'on' ? 'var(--lime)' : 'var(--text-4)',
              }}
            >
              {mark === 'freeze' ? '–' : mark === 'on' ? '✓' : dayLetters[i]}
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '11px 13px',
            borderRadius: 12,
            background: 'rgba(0,0,0,.24)',
          }}
        >
          <div
            style={{ flex: 'none', width: 8, height: 8, borderRadius: 999, background: 'var(--amber)' }}
          />
          <div
            style={{
              font: '400 12.5px/1.45 var(--font-body)',
              color: 'var(--text-3)',
              textWrap: 'pretty',
            }}
          >
            {progress.freezes} skip days banked. A missed day spends one instead of wiping the count,
            and badges never reset.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Eyebrow>Badges</Eyebrow>
          <div
            className="num"
            style={{ font: '500 11.5px/1 var(--font-body)', color: 'var(--text-4)' }}
          >
            {earned.size} of {BADGES.length}
          </div>
        </div>

        {BADGES.map((badge) => {
          const got = earned.has(badge.id);
          const have = Math.min(badge.get(progress), badge.need);

          return (
            <div
              key={badge.id}
              style={{
                padding: '14px 15px',
                borderRadius: 15,
                background: got ? 'oklch(0.82 0.17 130 / .07)' : 'var(--surface)',
                border: `1px solid ${got ? 'oklch(0.82 0.17 130 / .35)' : 'var(--line)'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                <div
                  className="num"
                  style={{
                    flex: 'none',
                    width: 42,
                    height: 42,
                    borderRadius: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    font: '700 13px/1 var(--font-display)',
                    background: got ? 'oklch(0.82 0.17 130 / .16)' : 'rgba(255,255,255,.05)',
                    border: `1px solid ${got ? 'var(--lime-line)' : 'var(--line-strong)'}`,
                    color: got ? 'var(--lime)' : 'var(--text-4)',
                  }}
                >
                  {badge.chip}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div
                    style={{
                      font: '700 15px/1.2 var(--font-display)',
                      color: got ? 'var(--text)' : 'var(--text-3)',
                    }}
                  >
                    {badge.name}
                  </div>
                  <div
                    style={{
                      font: '400 12px/1.35 var(--font-body)',
                      color: 'var(--text-4)',
                      textWrap: 'pretty',
                    }}
                  >
                    {badge.cond}
                  </div>
                </div>
                <div
                  className="num"
                  style={{ font: '600 11px/1 var(--font-display)', color: 'var(--text-4)' }}
                >
                  {got ? 'earned' : `${have}/${badge.need}`}
                </div>
              </div>

              {!got && (
                <div style={{ marginTop: 11 }}>
                  <Bar pct={(have / badge.need) * 100} tone="var(--amber)" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <Eyebrow>The count so far</Eyebrow>
        <Row label="Problems worked" value={progress.solved} />
        <Row label="Sets finished" value={progress.sets} />
        <Row label="Clean runs" value={progress.cleanRuns.length} />
        <Row label="Bench challenges done" value={progress.bench} />
      </div>

      {/* Settings lives here rather than behind a gear in the header — the
          home screen stays a single call to action, which is the point. */}
      <button
        type="button"
        onClick={onOpenSettings}
        style={{ ...ghostButton, width: '100%', flex: 'none' }}
      >
        Settings and backup
      </button>
    </Screen>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 15px',
        borderRadius: 13,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
      }}
    >
      <div style={{ font: '500 14px/1 var(--font-body)', color: 'var(--text-2)' }}>{label}</div>
      <div className="num" style={{ font: '700 17px/1 var(--font-display)', color: 'var(--lime)' }}>
        {value}
      </div>
    </div>
  );
}
